import type { ReactNode } from 'react';
import {
  ProjectFilterSelect,
  WorkerFilterSelect,
  type SelectedEntity,
} from '../EntityFilterSelect';

export type Density = 'day' | 'week' | 'month';

export interface FilterValue {
  region: string;
  office: string;
  serviceLine: string;
  /** Workers in scope; empty means every worker. */
  people: SelectedEntity[];
  /** Projects in scope; empty means every project. */
  projects: SelectedEntity[];
}

interface ScheduleFiltersProps {
  regions: string[];
  offices: string[];
  serviceLines: string[];
  value: FilterValue;
  onChange: (next: FilterValue) => void;
  density: Density;
  onDensityChange: (d: Density) => void;
  onTodayClick: () => void;
  onPrev: () => void;
  onNext: () => void;
  windowStart: string; // yyyy-MM-dd
  onWindowStartChange: (d: string) => void;
  /** Optional saved-filters control, rendered alongside the region/office/service-line selects. */
  filterMenu?: ReactNode;
}

const densities: { label: string; value: Density }[] = [
  { label: 'Day', value: 'day' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
];

export function ScheduleFilters({
  regions,
  offices,
  serviceLines,
  value,
  onChange,
  density,
  onDensityChange,
  onTodayClick,
  onPrev,
  onNext,
  windowStart,
  onWindowStartChange,
  filterMenu,
}: ScheduleFiltersProps) {
  const hasFilters =
    value.region || value.office || value.serviceLine ||
    value.people.length > 0 || value.projects.length > 0;

  return (
    <div className="py-3 flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white">
      {filterMenu}

      <WorkerFilterSelect
        selected={value.people}
        onChange={(people) => onChange({ ...value, people })}
      />

      <ProjectFilterSelect
        selected={value.projects}
        onChange={(projects) => onChange({ ...value, projects })}
      />

      <select
        value={value.region}
        onChange={(e) => onChange({ ...value, region: e.target.value })}
        className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
        aria-label="Filter by region"
      >
        <option value="">All regions</option>
        {regions.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>

      <select
        value={value.office}
        onChange={(e) => onChange({ ...value, office: e.target.value })}
        className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
        aria-label="Filter by office"
      >
        <option value="">All offices</option>
        {offices.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>

      <select
        value={value.serviceLine}
        onChange={(e) => onChange({ ...value, serviceLine: e.target.value })}
        className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
        aria-label="Filter by service line"
      >
        <option value="">All service lines</option>
        {serviceLines.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>

      {hasFilters && (
        <button
          type="button"
          onClick={() => onChange({ region: '', office: '', serviceLine: '', people: [], projects: [] })}
          className="text-xs text-brand-blue hover:underline"
        >
          Clear filters
        </button>
      )}

      <div className="flex-1" />

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPrev}
          aria-label="Previous window"
          className="border border-slate-300 rounded px-2 py-1 text-sm hover:bg-slate-100 text-brand-charcoal"
        >
          ◀
        </button>
        <button
          type="button"
          onClick={onTodayClick}
          className="border border-slate-300 rounded px-3 py-1 text-sm hover:bg-slate-100 text-brand-charcoal"
        >
          Today
        </button>
        <button
          type="button"
          onClick={onNext}
          aria-label="Next window"
          className="border border-slate-300 rounded px-2 py-1 text-sm hover:bg-slate-100 text-brand-charcoal"
        >
          ▶
        </button>
        <input
          type="date"
          value={windowStart}
          onChange={(e) => e.target.value && onWindowStartChange(e.target.value)}
          aria-label="Jump to date"
          className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
        />
      </div>

      <div role="group" aria-label="Density" className="flex items-center">
        {densities.map((d) => (
          <button
            key={d.value}
            type="button"
            onClick={() => onDensityChange(d.value)}
            className={`rounded-full px-3 py-1 text-xs border ${
              density === d.value
                ? 'bg-brand-blue text-white border-brand-blue'
                : 'text-brand-charcoal border-slate-300 hover:bg-slate-100'
            } ${d.value === 'day' ? 'rounded-r-none border-r-0' : d.value === 'month' ? 'rounded-l-none' : 'rounded-none border-r-0'}`}
            aria-pressed={density === d.value}
          >
            {d.label}
          </button>
        ))}
      </div>

    </div>
  );
}
